import { defaultEntryFilters } from "@app/constants/filters.defaults.tsx";
import { ActiveEnums } from "@app/data/engine.ts";
import { EntryFiltersModel } from "@app/data-hooks/use-entries-data.ts";
import useStaffData from "@app/data-hooks/use-staff-data.ts";
import useWorkLocationData from "@app/data-hooks/use-work-location-data.ts";
import useAuth from "@app/hooks/use-auth.ts";
import { Grid2, Stack } from "@mui/material";
import { Multiselect, QueryStatusRouter, Select } from "bi-lib";
import { useEffect, useMemo } from "react";
import AllYesNoCheckbox from "./all-yes-no-checkbox.tsx";
import DateInput from "./date-input.tsx";
import FiltersContainer from "./filters-container.tsx";
import { parseDate } from "@app/helpers/time-helper.ts";

interface Props {
    filters: EntryFiltersModel;
    setFilters: (updater: (cb: EntryFiltersModel) => EntryFiltersModel) => void;
    postingMode: boolean;
    rateType: "billing" | "cost" | "internal";
    setRateType: (value: "billing" | "cost" | "internal") => void;
}

export default function EntryFilters({ filters, setFilters, postingMode, rateType, setRateType }: Props) {
    const { staffingList, staffAssignmentsList } = useStaffData({
        staffIds: filters.staffId,
        activeState: ActiveEnums.ActiveOnly,
    });
    const { workLocationsList } = useWorkLocationData({ assignmentIds: filters.assignmentId });

    const { userData } = useAuth();

    useEffect(() => {
        if (staffingList.data == undefined || !setFilters) {
            return;
        }
        const tempStaffIds = filters.staffId
            ?.split(",")
            .map((x) => parseInt(x))
            .filter((x) => staffingList.data.some((y) => y.staffingId == x))
            .join(",");
        setFilters(() => ({ ...filters, staffId: tempStaffIds }));
    }, [staffingList.data]);

    const isOutsideConsultant = useMemo(() => userData?.IsOutsideConsultant ?? true, []);

    return (
        <FiltersContainer
            onReset={() =>
                setFilters(() => ({
                    ...defaultEntryFilters,
                    isPosted: postingMode ? ActiveEnums.NotActive : defaultEntryFilters.isPosted,
                }))
            }
        >
            <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Stack spacing={1}>
                        <DateInput
                            label={"Start Date"}
                            value={filters.from}
                            onChange={(value) => {
                                const valueDate = parseDate(value);
                                const toDate = parseDate(filters.to);
                                if (!valueDate) {
                                    setFilters((prev) => ({ ...prev, from: value }));
                                    return;
                                }
                                setFilters((prev) => ({ ...prev, from: value }));
                                if (toDate && valueDate > toDate) {
                                    setFilters((prev) => ({ ...prev, to: value }));
                                }
                            }}
                            gap={0}
                        />
                        <DateInput
                            label={"End Date"}
                            value={filters.to}
                            onChange={(value) => {
                                const valueDate = parseDate(value);
                                const fromDate = parseDate(filters.from);
                                if (!valueDate) {
                                    setFilters((prev) => ({ ...prev, to: value }));
                                    return;
                                }
                                setFilters((prev) => ({ ...prev, to: value }));
                                if (fromDate && valueDate < fromDate) {
                                    setFilters((prev) => ({ ...prev, from: value }));
                                }
                            }}
                            gap={0}
                        />
                    </Stack>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4, md: 3 }}>
                    <Stack spacing={1}>
                        <QueryStatusRouter queryHook={staffingList}>
                            <Multiselect
                                variant={"outlined"}
                                label={"Staff"}
                                value={filters.staffId ? (filters.staffId.split(",").map((x) => +x) ?? []) : []}
                                onChange={(e) =>
                                    setFilters(() => ({
                                        ...filters,
                                        staffId: e.join(","),
                                        assignmentId: undefined,
                                    }))
                                }
                                disabled={isOutsideConsultant}
                                options={staffingList.data?.map((x) => ({
                                    label: `${x.lastName}, ${x.firstName}`,
                                    value: x.staffingId,
                                }))}
                            />
                        </QueryStatusRouter>
                        <QueryStatusRouter queryHook={staffingList}>
                            <Multiselect
                                label={"Assignments"}
                                value={
                                    !filters.assignmentId ? [] : (filters.assignmentId.split(",").map((x) => +x) ?? [])
                                }
                                onChange={(e) =>
                                    setFilters(() => ({
                                        ...filters,
                                        assignmentId: e.join(","),
                                        phaseId: undefined,
                                        locationId: undefined,
                                    }))
                                }
                                options={staffAssignmentsList.data?.map((x) => ({
                                    label: x.displayName,
                                    value: x.assignmentId,
                                }))}
                            />
                        </QueryStatusRouter>
                    </Stack>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                    <Stack spacing={1}>
                        <QueryStatusRouter queryHook={workLocationsList}>
                            <Select
                                label={"Locations"}
                                value={filters.locationId}
                                disabled={filters.assignmentId == undefined || filters.assignmentId == ""}
                                onChange={(e) =>
                                    setFilters(() => ({
                                        ...filters,
                                        locationId: e,
                                    }))
                                }
                                options={workLocationsList.data?.map((x) => ({
                                    label: x.displayName,
                                    value: x.locationId,
                                }))}
                            />
                        </QueryStatusRouter>
                    </Stack>
                </Grid2>
                {!isOutsideConsultant && (
                    <>
                        <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                            <Stack spacing={1}>
                                <Select
                                    label={"Rate Type"}
                                    value={rateType}
                                    onChange={(e) => setRateType(e as "billing" | "cost" | "internal")}
                                    options={[
                                        { label: "Billing", value: "billing" },
                                        { label: "Cost", value: "cost" },
                                        { label: "Internal", value: "internal" },
                                    ]}
                                />
                                <AllYesNoCheckbox
                                    value={filters.isBillable ?? ActiveEnums.All}
                                    onChange={(value) => setFilters(() => ({ ...filters, isBillable: value }))}
                                    label={"Billable"}
                                />
                            </Stack>
                        </Grid2>
                        <Grid2 size={{ xs: 6, sm: 4, md: 2 }}>
                            <AllYesNoCheckbox
                                value={filters.InvoiceVisible ?? ActiveEnums.All}
                                onChange={(value) => setFilters(() => ({ ...filters, InvoiceVisible: value }))}
                                label={"Invoice Visible"}
                            />
                            <AllYesNoCheckbox
                                value={filters.Reimbursable ?? ActiveEnums.All}
                                onChange={(value) => setFilters(() => ({ ...filters, Reimbursable: value }))}
                                label={"Reimbursable"}
                            />
                            <AllYesNoCheckbox
                                value={filters.isPosted ?? ActiveEnums.All}
                                onChange={(value) => setFilters(() => ({ ...filters, isPosted: value }))}
                                label={"Posted"}
                                disabled={postingMode}
                            />
                        </Grid2>
                    </>
                )}
            </Grid2>
        </FiltersContainer>
    );
}
